import React, { useRef, useState, useEffect } from 'react';
import { Camera, X, AlertCircle } from 'lucide-react';

const BARCODE_FORMATS = [
  'code_128', 'code_39', 'code_93', 'codabar',
  'ean_13', 'ean_8', 'upc_a', 'upc_e',
  'qr_code', 'data_matrix', 'itf',
];

// ── 실시간 카메라 스캔 모달 (HTTPS / localhost 전용) ──────────────────
// continuous=true 이면 스캔 후 닫히지 않고 계속 스캔 (연속 모드)
function LiveModal({ color, onScan, onClose, continuous = false, headerExtra = '' }) {
  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const activeRef = useRef(true);
  const coolRef   = useRef(false);
  const [err, setErr] = useState('');
  const [ready, setReady] = useState(false);
  const [flashText, setFlashText] = useState('');

  useEffect(() => {
    const detector = new window.BarcodeDetector({ formats: BARCODE_FORMATS });

    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      .then(stream => {
        if (!activeRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.play().then(() => {
          setReady(true);
          // 비동기 스캔 루프 (300ms 간격)
          const scan = async () => {
            while (activeRef.current) {
              if (video.readyState >= 2) {
                try {
                  const codes = await detector.detect(video);
                  if (codes.length > 0 && activeRef.current) {
                    const val = codes[0].rawValue;
                    if (continuous) {
                      // 연속 모드: 쿨다운 중이 아닐 때만 스캔
                      if (!coolRef.current) {
                        coolRef.current = true;
                        setFlashText(val);
                        onScan(val);
                        await new Promise(r => setTimeout(r, 900));
                        if (activeRef.current) setFlashText('');
                        coolRef.current = false;
                      }
                    } else {
                      activeRef.current = false;
                      onScan(val);
                      return;
                    }
                  }
                } catch {}
              }
              await new Promise(r => setTimeout(r, 300));
            }
          };
          scan();
        });
      })
      .catch(e => {
        if (!activeRef.current) return;
        if (e.name === 'NotAllowedError') {
          setErr('카메라 권한이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해 주세요.');
        } else {
          setErr(e.message || '카메라를 시작할 수 없습니다.');
        }
      });

    return () => {
      activeRef.current = false;
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [onScan, continuous]);

  const borderColor = flashText ? '#22c55e' : color;

  return (
    <div style={overlayStyle}>
      <div style={{ width: '95vw', maxWidth: 440, background: '#111', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
        {/* 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: color }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Camera size={16} color="#fff" />
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>실시간 바코드 스캔</span>
            {headerExtra && <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>{headerExtra}</span>}
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, padding: '5px 9px', cursor: 'pointer', color: '#fff', display: 'flex' }}>
            <X size={16} />
          </button>
        </div>

        {err ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <AlertCircle size={32} color="#f87171" style={{ marginBottom: 12 }} />
            <p style={{ color: '#f87171', fontSize: 14, margin: '0 0 8px', lineHeight: 1.5 }}>{err}</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <video
              ref={videoRef}
              style={{ width: '100%', display: 'block', maxHeight: '72vh', objectFit: 'cover' }}
              playsInline muted
            />
            {ready && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                {/* 스캔 가이드 박스 */}
                <div style={{ width: '72%', height: '32%', border: `2.5px solid ${borderColor}`, borderRadius: 12, boxShadow: `0 0 0 2000px rgba(0,0,0,0.48)`, position: 'relative', transition: 'border-color 0.2s' }}>
                  {/* 모서리 강조 */}
                  {[['0%','0%'],['100%','0%'],['0%','100%'],['100%','100%']].map(([l, t], i) => (
                    <div key={i} style={{
                      position: 'absolute', width: 18, height: 18,
                      borderTop: i < 2 ? `3px solid ${borderColor}` : 'none',
                      borderBottom: i >= 2 ? `3px solid ${borderColor}` : 'none',
                      borderLeft: i % 2 === 0 ? `3px solid ${borderColor}` : 'none',
                      borderRight: i % 2 === 1 ? `3px solid ${borderColor}` : 'none',
                      top: t, left: l,
                      transform: `translate(${i % 2 === 1 ? '-100%' : '0'}, ${i >= 2 ? '-100%' : '0'})`,
                      borderRadius: i === 0 ? '3px 0 0 0' : i === 1 ? '0 3px 0 0' : i === 2 ? '0 0 0 3px' : '0 0 3px 0',
                      transition: 'border-color 0.2s',
                    }} />
                  ))}
                </div>
              </div>
            )}
            {/* 연속 모드 스캔 성공 피드백 */}
            {flashText && (
              <div style={{ position: 'absolute', bottom: 48, left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ background: '#22c55e', color: 'white', padding: '8px 20px', borderRadius: 25, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  ✓ {flashText}
                </div>
              </div>
            )}
            <p style={{ margin: 0, padding: '10px', textAlign: 'center', color: '#ccc', fontSize: 12 }}>
              바코드를 가이드 박스 안에 맞춰주세요
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────
// continuous=true : 스캔 후 모달 유지 (연속 스캔), false(기본): 1회 스캔 후 닫힘
// buttonStyle / buttonContent : 버튼 외관 커스터마이징
// headerExtra : 모달 헤더에 추가로 표시할 텍스트 (예: "3/5본")
const CameraScanner = ({ onScan, color = '#185FA5', continuous = false, buttonStyle: customBtnStyle, buttonContent, headerExtra = '' }) => {
  const [showLive, setShowLive]       = useState(false);
  const [fileScanning, setFileScanning] = useState(false);
  const [fileError, setFileError]     = useState('');
  const fileInputRef = useRef(null);

  const openFileInput = () => {
    setShowLive(false);
    setFileError('');
    fileInputRef.current?.click();
  };

  const handleClick = () => {
    setFileError('');
    // HTTPS 또는 localhost + BarcodeDetector 지원 시 → 실시간 모달
    const canLive = window.isSecureContext
      && !!navigator.mediaDevices?.getUserMedia
      && ('BarcodeDetector' in window);
    if (canLive) {
      setShowLive(true);
    } else {
      openFileInput();
    }
  };

  const handleScan = (code) => {
    if (!continuous) setShowLive(false);  // 연속 모드에선 닫지 않음
    onScan(code);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileScanning(true);
    setFileError('');
    let objUrl;
    try {
      if (!('BarcodeDetector' in window)) {
        setFileError('바코드 자동 인식이 지원되지 않습니다. 직접 입력해 주세요.');
        return;
      }
      objUrl = URL.createObjectURL(file);
      const detector = new window.BarcodeDetector({ formats: BARCODE_FORMATS });
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = objUrl;
      });
      const codes = await detector.detect(img);
      if (codes.length > 0) {
        onScan(codes[0].rawValue);
        setFileError('');
      } else {
        setFileError('바코드를 인식하지 못했습니다. 바코드에 더 가까이서 선명하게 촬영해 주세요.');
      }
    } catch (err) {
      setFileError('오류: ' + (err.message || String(err)));
    } finally {
      if (objUrl) URL.revokeObjectURL(objUrl);
      setFileScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
      {/* 파일 입력 (카메라 앱 호출) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <button
          type="button"
          onClick={handleClick}
          disabled={fileScanning}
          style={customBtnStyle || {
            padding: '10px 14px',
            background: fileScanning ? '#e5e7eb' : color,
            color: fileScanning ? '#9ca3af' : '#fff',
            border: 'none',
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 13,
            cursor: fileScanning ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
            transition: 'background 0.15s',
          }}
        >
          {buttonContent || (<><Camera size={15} />{fileScanning ? '인식 중...' : '카메라 스캔'}</>)}
        </button>
        {fileError && (
          <span style={{ fontSize: 11, color: '#DC2626', fontWeight: 600, maxWidth: 220, lineHeight: 1.4 }}>
            {fileError}
          </span>
        )}
      </div>

      {showLive && (
        <LiveModal
          color={color}
          onScan={handleScan}
          onClose={() => setShowLive(false)}
          continuous={continuous}
          headerExtra={headerExtra}
        />
      )}
    </>
  );
};

const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.88)',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export default CameraScanner;
